import { FilterAttributeOption } from '@df/catalog-ui/listing/filter/filter-attribute-option';
import { IFilterState, IMethodWrapEvent, LISTING_GET_FILTERS, ListingFilter, TAttrOptionSelection } from '@df/catalog-ui/listing/listing';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { IProductData, IProductDataV2, Product } from '@df/catalog/product/product';
import { CacheService } from '@df/core/cache/cache';
import { NgInject } from '@df/ng/ng-inject';

export interface IFilterStockOption extends Catalog.IAttributeOption {
    checkProduct(product: Product<IProductData>): boolean;
}

export const IN_STOCK_FILTER_ID = 'in_stock';

@ListingFilter({
    filterType: 'stock'
})
export class FilterStock<T extends Product<IProductData>> extends FilterAttributeOption<T> {
    @NgInject(CacheService)
    private cacheService: CacheService;

    protected indexes: {
        [attributeCode: string]: {
            [attributeValue: number]: { [modelId: number]: boolean };
        };
    };

    override get options() {
        return [
            {
                value: 1,
                label: 'Show',
                checkProduct: (product: Product<IProductData>) => this.withOutOfStock(product)
            },
            {
                value: 2,
                label: 'Hide',
                checkProduct: (product: Product<IProductData>) => this.withoutOutOfStock(product)
            }
        ];
    }

    /**
     * Gets index related to attribute from `Resource`
     */
    override getIndex() {
        if (!this.indexes || !this.indexes[this.id]) {
            if (!this.indexes) {
                this.indexes = {};
            }

            this.indexes[this.id] = {};

            const index = {};

            this.options?.forEach(option => {
                const optionIndex = {};

                this.listing.getSourceModels().forEach(product => {
                    const checkResult = option.checkProduct(product);

                    if (checkResult) {
                        optionIndex[product.id] = true;
                    }
                });

                index[option.value] = optionIndex;
            });

            this.indexes[this.id] = index;
        }

        return this.indexes[this.id];
    }

    protected override onInit() {
        super.onInit();

        this.app.events.on<IMethodWrapEvent<IFilterState>>(LISTING_GET_FILTERS).subscribe(transport => {
            const cb = transport.cb;
            transport.cb = () => {
                // read hash params
                const data = cb() || {};

                if (!data[IN_STOCK_FILTER_ID]) {
                    data[IN_STOCK_FILTER_ID] = this.options[0].label;
                }

                return data;
            };
        });
    }

    protected withOutOfStock(product: Product<IProductData>): boolean {
        const oosVisibility = (<Product<IProductDataV2>>product.parent).data?.oos_visibility;
        const inStock = this.withoutOutOfStock(product);

        return (
            !!product &&
            (product.inStock ||
                (!this.app.data.showOutOfStock && inStock) ||
                (!!this.app.data.showOutOfStock && !inStock && !!oosVisibility))
        );
    }

    protected withoutOutOfStock(product: Product<IProductData>): boolean {
        return product.inStock && product.deepStockCheck(this.getSelection(), true);
    }

    protected getSelection(): TAttrOptionSelection {
        return this.cacheService.get(['filterStockSelection', ...this.listing.filterSelection], () => {
            const selection: TAttrOptionSelection = {};

            this.listing.filters.forEach(filter => {
                if (filter.config.type === 'attribute-option' || filter.config.type === 'attribute' || filter.config.type === 'stock') {
                    const filterSelection =
                        (filter.selection as number[]) || ((filter['options'] as Catalog.IAttributeOption[]) || []).map(i => i.value);

                    if (filterSelection && Array.isArray(filterSelection)) {
                        selection[filter.id] = filterSelection;
                    }
                }
            });

            return selection;
        });
    }
}
